import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function Cart() {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      dispatch({ type: 'REMOVE_FROM_CART', payload: id });
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
    }
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: id });
  };

  const subtotal = state.cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.18;
  const shipping = subtotal > 1000 ? 0 : 99;
  const total = subtotal + tax + shipping;

  if (state.cart.length === 0) {
    return (
      <div className="min-h-screen bg-black py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="w-16 h-16 sm:w-24 sm:h-24 text-gray-600 mx-auto mb-6 sm:mb-8" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">Your cart is empty</h1>
          <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
            Looks like you haven't added any items to your cart yet.
          </p>
          <Link 
            to="/products" 
            className="bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-colors inline-flex items-center space-x-2"
          >
            <span>Continue Shopping</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/products" className="inline-flex items-center space-x-2 text-red-400 hover:text-red-300 mb-6 sm:mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm sm:text-base">Continue Shopping</span>
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {state.cart.map((item) => (
              <div key={item.id} className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800">
                <div className="flex items-center space-x-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-2xl"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold mb-1 text-sm sm:text-base truncate">{item.name}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm mb-2 line-clamp-2">{item.description}</p>
                    <div className="flex items-center space-x-2">
                      {item.originalPrice && (
                        <span className="text-gray-500 line-through text-xs sm:text-sm">
                          ₹{item.originalPrice.toLocaleString()}
                        </span>
                      )}
                      <span className="text-green-400 font-bold text-sm sm:text-base">
                        ₹{item.price.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="bg-gray-800 hover:bg-gray-700 text-white w-8 h-8 rounded-full flex items-center justify-center"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      
                      <span className="text-white font-semibold w-8 text-center text-sm">
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="bg-gray-800 hover:bg-gray-700 text-white w-8 h-8 rounded-full flex items-center justify-center"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="text-center">
                      <p className="text-white font-bold text-sm sm:text-base">
                        ₹{(item.price * item.quantity).toLocaleString()}
                      </p>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-400 hover:text-red-300 mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800 sticky top-24">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">Order Summary</h2>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between text-gray-300 text-sm sm:text-base">
                  <span>Subtotal ({state.cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between text-gray-300 text-sm sm:text-base">
                  <span>GST (18%)</span>
                  <span>₹{tax.toFixed(0)}</span>
                </div>
                
                <div className="flex justify-between text-gray-300 text-sm sm:text-base">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between text-base sm:text-lg font-bold text-white">
                    <span>Total</span>
                    <span className="text-green-400">₹{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-full font-semibold transition-colors mt-4 sm:mt-6"
              >
                Proceed to Checkout
              </button>

              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400">
                  Free shipping on orders above ₹1,000
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}